import { Fragment, useEffect, useState } from "react";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { LayerContent } from "../style";
import { handlePointClicked, transformDataForBarChart } from "../formUtils";
import SectionModal, { DefaultContainer } from "./SectionModal";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

// Section 12 — Caregivers reporting multiple incidents. Mirrors the Fall Analysis caregivers section:
// caregiver / number / %, a horizontal bar, and a click-through drill-down to the incident list.
// Reads woundAnalysisIncidents.caregiversReporting (caregiverBreakdown + drillDownByCaregiver).
const CaregiversReporting = ({ setClickedIncident }: any) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [caregiver, setCaregiver] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [caregiverDetails, setCaregiverDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  type CaregiverRow = {
    caregiverName: string;
    count: number;
    percentage: number;
  };

  const adjustedTableData = (data: any): CaregiverRow[] => {
    const returnData: CaregiverRow[] = [];
    if (data?.length) {
      let countTotal = 0;
      for (let i = 0; i < data.length; i++) {
        returnData.push({
          caregiverName: data[i].caregiverName,
          count: data[i].count,
          percentage: data[i].percentage,
        });
        countTotal += data[i].count;
      }
      // Total % is fixed at 100 when there's data (summing rounded row percentages can drift to
      // 99/101); matches how the other analytics tables render the Total row.
      returnData.push({
        caregiverName: "Total",
        count: countTotal,
        percentage: countTotal > 0 ? 100 : 0,
      });
    }
    return returnData;
  };

  useEffect(() => {
    if (woundAnalysisIncidents !== undefined) {
      const temp = woundAnalysisIncidents?.caregiversReporting?.caregiverBreakdown;
      const transformed = adjustedTableData(temp);
      setTableData(transformed);
      setBarData(transformDataForBarChart(transformed, "Caregiver Count"));
    }
  }, [woundAnalysisIncidents]);

  const title = "Caregivers Reporting Multiple Incidents";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setCaregiver(label);
    setCaregiverDetails(
      woundAnalysisIncidents?.caregiversReporting?.drillDownByCaregiver?.[label] || []
    );
    setShowModal(true);
  };

  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    const newGrid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="none" rows={{ data: TotalRow }}>
          <GridColumn field="caregiverName" title="Caregiver" />
          <GridColumn field="count" title="Number" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="percentage" title="%" {...(cellPercentageProps as GridColumnProps)} />
        </Grid>
      </Fragment>
    );
    setGrid(newGrid);
  }, [tableData]);

  return (
    <LayerContent>
      <DefaultContainer
        title={title}
        grid={grid}
        handleBarClicked={(chart: any) => handleBarClicked(chart)}
        barData={barData}
        barType="horizontal"
      />
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={caregiverDetails}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${caregiver}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: caregiverDetails,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${caregiver}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              caregiverDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${caregiver}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default CaregiversReporting;
